import { useState } from 'react';
import { bucketLabel } from './salesPeriods';
import { money } from '../../core/format/currency';
import type { SalesGroupBy, SalesReport } from '../../core/types/adminRequests';
type Props = {
  series: SalesReport['series'];
  groupBy: SalesGroupBy;
};
const unit: Record<SalesGroupBy, string> = {
  day: 'dag',
  week: 'week',
  month: 'maand'
};

/**
 * Omzet exclusief btw per dag, week of maand, als eenvoudige staafjes. Een dag met meer
 * terugbetaald dan verkocht staat op nul (de lijst eronder toont het echte bedrag).
 * Het bedrag verschijnt bij aanwijzen met de muis én bij tikken (telefoon heeft geen hover).
 * Voor schermlezers dezelfde cijfers als tabel.
 */
export function SalesChart({
  series,
  groupBy
}: Props) {
  const [active, setActive] = useState<string | null>(null);
  const max = Math.max(1, ...series.map(s => s.revenueExVatCents));
  const empty = series.every(s => s.revenueExVatCents <= 0);
  // Niet elk label past: hoogstens ~12 onder de grafiek.
  const every = Math.max(1, Math.ceil(series.length / 12));
  const last = series.length - 1;
  return <figure className="relative" data-id="element-3927" data-mp="3nc87yh8jnlg4">
      {empty && <p className="absolute inset-x-0 top-1/3 text-center text-[13px] text-muted" data-id="element-3928" data-mp="3nanbgm2o5sfm">Nog geen verkopen in deze periode.</p>}
      <div className="flex h-44 items-end gap-[3px] sm:h-52" aria-hidden="true" onPointerLeave={() => setActive(null)} data-id="element-3929" data-mp="3s9i5kk6373f1">
        {series.map((s, i) => {
        const h = Math.max(0, s.revenueExVatCents) / max;
        const shown = active === s.date;
        // Aan de randen het label naar binnen laten wijzen, zodat het niet buiten het kader valt.
        const place = i < 2 ? 'left-0' : i > last - 2 ? 'right-0' : 'left-1/2 -translate-x-1/2';
        return <div key={s.date} className="relative flex h-full min-w-0 flex-1 cursor-default items-end" onPointerEnter={e => e.pointerType === 'mouse' && setActive(s.date)} onClick={() => setActive(a => a === s.date ? null : s.date)} data-id="element-3930" data-mp="3ecutfa5kc6oj" data-mpi={i}>
              <div className={`w-full rounded-t-sm transition-colors duration-150 ease-soft ${h > 0 ? shown ? 'bg-clay' : 'bg-clay/80' : 'bg-line'}`} style={{
            height: h > 0 ? `${Math.max(2, h * 100)}%` : '2px'
          }} data-id="element-3931" data-mp="2dsghdsw9lzfn" />
              {shown && <span className={`pointer-events-none absolute bottom-full z-raised mb-1 whitespace-nowrap rounded bg-ink px-2 py-1 text-[11px] text-shell ${place}`} data-id="element-3932" data-mp="2im1i3sp8t7ij">
                  {bucketLabel(s.date, groupBy)} · {money(s.revenueExVatCents)}
                </span>}
            </div>;
      })}
      </div>
      <div className="mt-1.5 flex gap-[3px] text-[11px] tabular-nums text-muted" aria-hidden="true" data-id="element-3933" data-mp="1zv1p8gemndjz">
        {series.map((s, i) => <span key={s.date} className="min-w-0 flex-1 truncate text-center" data-id="element-3934" data-mp="3g7yynkk2b2ru" data-mpi={i}>
            {i % every === 0 ? bucketLabel(s.date, groupBy) : ''}
          </span>)}
      </div>
      <figcaption className="sr-only" data-id="element-3935" data-mp="x1wiegtt37i5">Omzet exclusief btw per {unit[groupBy]}</figcaption>
      <table className="sr-only" data-id="element-3936" data-mp="121sabhp8b8hc">
        <thead data-id="element-3937" data-mp="12221v9px9np5">
          <tr data-id="element-3938" data-mp="122k9f2i1bocd">
            <th scope="col" data-id="element-3939" data-mp="11ywd5vl7gnty">{unit[groupBy]}</th>
            <th scope="col" data-id="element-3940" data-mp="o2j7djq1aavb">Omzet excl. btw</th>
          </tr>
        </thead>
        <tbody data-id="element-3941" data-mp="t0b18ja8o725">
          {series.map(s => <tr key={s.date} data-id="element-3942" data-mp="t0ugttcf2jd0">
              <td data-id="element-3943" data-mp="1gq58mlbsh3f0">{s.date}</td>
              <td data-id="element-3944" data-mp="1gpwxaxvbe65x">{money(s.revenueExVatCents)}</td>
            </tr>)}
        </tbody>
      </table>
    </figure>;
}